import { Paper, Table, TableContainer } from '@mui/material'
import { useState, useMemo } from 'react'
import InvoicesTableHeaders from './invoice-table-header/invoice-table-header'
import InvoiceTableBody from './invoice-table-body/invoice-table-body'
import { TransformInvoiceResponse, InvoiceType } from 'store/invoices/invoices.types'
import { listTableSx } from 'components/list-table/list-table.styles'

type SortColumn = 'invoice' | 'company' | 'aging' | 'amount' | null
type SortDirection = 'asc' | 'desc'

const DEFAULT_SORT_DIRECTION: Record<Exclude<SortColumn, null>, SortDirection> = {
  invoice: 'desc',
  company: 'desc',
  aging: 'desc',
  amount: 'desc',
}

interface InvoiceTableProps {
  invoices: TransformInvoiceResponse[]
  onSelectedInvoicesChange?: (selectedIds: Set<string>) => void
  invoiceType: InvoiceType
  showBatchColumn?: boolean
  onBatchClick?: (batchId: number) => void
  groupByBatch?: boolean
}

const InvoiceTable = ({
  invoices,
  onSelectedInvoicesChange,
  invoiceType,
  showBatchColumn = false,
  onBatchClick,
  groupByBatch = false,
}: InvoiceTableProps) => {
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set())
  const [sort, setSort] = useState<{ column: SortColumn; direction: SortDirection }>({
    column: 'invoice',
    direction: 'desc',
  })
  const sortColumn = sort.column
  const sortDirection = sort.direction

  const allInvoiceIds = useMemo(() => {
    const ids = new Set<string>()
    invoices?.forEach((invoice) => {
      invoice.invoiceData.forEach((item) => {
        ids.add(item.id)
      })
    })
    return ids
  }, [invoices])

  const allSelected = allInvoiceIds.size > 0 && selectedInvoiceIds.size === allInvoiceIds.size
  const someSelected = selectedInvoiceIds.size > 0 && selectedInvoiceIds.size < allInvoiceIds.size

  const handleInvoiceSelect = (invoiceId: string, selected: boolean) => {
    const newSelected = new Set(selectedInvoiceIds)
    if (selected) {
      newSelected.add(invoiceId)
    } else {
      newSelected.delete(invoiceId)
    }
    setSelectedInvoiceIds(newSelected)
    onSelectedInvoicesChange?.(newSelected)
  }

  const handleSortChange = (column: Exclude<SortColumn, null>) => {
    setSort((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { column, direction: DEFAULT_SORT_DIRECTION[column] }
    })
  }

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedInvoiceIds(new Set(allInvoiceIds))
      onSelectedInvoicesChange?.(new Set(allInvoiceIds))
    } else {
      setSelectedInvoiceIds(new Set())
      onSelectedInvoicesChange?.(new Set())
    }
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 0,
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        overscrollBehaviorY: 'contain',
      }}
    >
      <Table aria-label="invoice table" stickyHeader sx={listTableSx}>
        <InvoicesTableHeaders 
          allSelected={allSelected}
          someSelected={someSelected}
          onSelectAll={handleSelectAll}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          showBatchColumn={showBatchColumn}
        />
        <InvoiceTableBody 
          invoices={invoices} 
          selectedInvoiceIds={selectedInvoiceIds}
          onInvoiceSelect={handleInvoiceSelect}
          invoiceType={invoiceType}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          showBatchColumn={showBatchColumn}
          onBatchClick={onBatchClick}
          groupByBatch={groupByBatch}
        />
      </Table>
    </TableContainer>
  )
}

export default InvoiceTable
